import { Card } from '@chakra-ui/react';
import {
  IAccountRespSchema,
  IPriceUnderProductSchemaUI,
  IQuoteItemRespSchema,
  IQuoteOfferingRespSchema,
  IRateResSchema,
  PriceModelEnum,
} from '@monetize/types/app';
import { IOrderedObj } from '@monetize/types/common';
import { formatNumber, sortByProductPosition } from '@monetize/utils/core';
import { Fragment, useState } from 'react';
import { MdInfo } from 'react-icons/md';
import { handleApiErrorToast } from '../../../api/axios';
import { OFFERINGS } from '../../../constants';
import { PRODUCT_TYPE_DISPLAY_SHORT } from '../../../constants/products';
import { useOfferingRate } from '../../../hooks';
import { getPricesFromProductObject } from '../../../routes/ProductCatalog/Offerings/components/Rate/RatePriceUtils';
import {
  MButton,
  MCard,
  MCheckbox,
  MDivider,
  MFlex,
  MIcon,
  MTable,
  MTbody,
  MTd,
  MText,
  MTh,
  MThead,
  MTooltip,
  MTr,
} from '../../Monetize';
import { MCurrency } from '../../Monetize/MCurrency';
import { prepareRateRequestData, resolveProductTiers } from './utils';

interface PricesDrawerPriceTierLockingProps {
  account: IAccountRespSchema | Pick<IAccountRespSchema, 'id' | 'accountName'>;
  offeringRate: IRateResSchema;
  productsWithPrices: IPriceUnderProductSchemaUI[];
  allProductsWithPrices: IPriceUnderProductSchemaUI[];
  productsObj: IOrderedObj<IQuoteItemRespSchema>;
  currency: string;
  eligibleProductIds: ReadonlySet<string>;
  quoteOffering: IQuoteOfferingRespSchema;
  onRateSaved?: (rate: IRateResSchema) => void;
  onSavingChange?: (isSaving: boolean) => void;
  onClose: () => void;
}

export const PricesDrawerPriceTierLocking = ({
  account,
  offeringRate,
  productsWithPrices,
  allProductsWithPrices,
  productsObj,
  currency,
  eligibleProductIds,
  quoteOffering,
  onRateSaved,
  onSavingChange,
  onClose,
}: PricesDrawerPriceTierLockingProps) => {
  const { createOfferingRate, loading: isSaving } = useOfferingRate();

  // Products with their price tiers collapsed to the one matching the quote quantity
  const tierResolvedProducts = resolveProductTiers(
    productsWithPrices,
    eligibleProductIds,
    quoteOffering,
  );

  const eligibleProducts = sortByProductPosition(
    tierResolvedProducts
      .filter((product) => eligibleProductIds.has(product.id))
      .map((item) => ({
        ...item,
        position: productsObj.byId[item.id]?.position ?? 0,
      })),
  );

  // Eligible products whose price model supports checkbox locking (non-CUSTOM, non-empty prices)
  const lockableProducts = eligibleProducts.filter(
    (product) =>
      product.prices.length > 0 &&
      product.prices[0].priceModel !== PriceModelEnum.CUSTOM,
  );

  const [lockedState, setLockedState] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(lockableProducts.map(({ id }) => [id, false])),
  );

  const allLocked =
    lockableProducts.length > 0 &&
    lockableProducts.every((product) => lockedState[product.id]);
  const someLocked =
    lockableProducts.some((product) => lockedState[product.id]) && !allLocked;

  const handleToggleRow = (productId: string, checked: boolean) => {
    setLockedState((prev) => ({ ...prev, [productId]: checked }));
  };

  const handleSelectAll = (checked: boolean) => {
    setLockedState(
      Object.fromEntries(lockableProducts.map(({ id }) => [id, checked])),
    );
  };

  const handleSave = async () => {
    const resolvedById = new Map(tierResolvedProducts.map((p) => [p.id, p]));

    const productsToSave = allProductsWithPrices.map((product) => {
      if (!eligibleProductIds.has(product.id)) {
        return product;
      }
      const resolvedProduct = resolvedById.get(product.id);
      if (!resolvedProduct) {
        return product;
      }
      const isCustomPriceModel =
        resolvedProduct.prices[0]?.priceModel === PriceModelEnum.CUSTOM;
      if (!isCustomPriceModel && !lockedState[product.id]) {
        return product;
      }
      return resolvedProduct;
    });

    const { products, ...rest } = prepareRateRequestData(
      account,
      productsToSave,
      offeringRate,
    );
    const data = { ...rest, prices: getPricesFromProductObject(products) };

    try {
      onSavingChange?.(true);
      const savedRate = await createOfferingRate(
        data,
        offeringRate.offering.id,
      );
      if (savedRate) {
        onRateSaved?.(savedRate);
      }
      onClose();
    } catch (ex) {
      handleApiErrorToast(ex);
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleCancel = () => {
    setLockedState(
      Object.fromEntries(lockableProducts.map(({ id }) => [id, false])),
    );
    onClose();
  };

  return (
    <MCard variant="borderless" mt="2">
      <Card.Body>
        {eligibleProducts.length === 0 ? (
          <MText color="tGray.darkPurple" fontSize="sm">
            No products are eligible for price tier locking.
          </MText>
        ) : (
          <>
            {lockableProducts.length > 0 && (
              <MFlex
                justify="space-between"
                align="center"
                gap={2}
                pr={2}
                pb={2}
              >
                <MFlex align="center" gap={1}>
                  <MText fontWeight="semibold">Price Tier Lock</MText>
                  <MTooltip
                    label="Locking creates an account-specific rate where the current quantity becomes the minimum and the current tier price applies to all future quantities (∞)."
                    placement="top"
                  >
                    <MFlex alignItems="center">
                      <MIcon as={MdInfo} color="tPurple.base" />
                    </MFlex>
                  </MTooltip>
                </MFlex>
                <MFlex align="center" gap={2}>
                  <MText fontWeight="semibold">Select all</MText>
                  <MCheckbox
                    isChecked={allLocked}
                    isIndeterminate={someLocked}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    aria-label="Select all"
                  />
                </MFlex>
              </MFlex>
            )}
            {eligibleProducts.map(
              ({ id, prices, name, productType }, index) => {
                const isLastRow = eligibleProducts.length - 1 === index;
                return (
                  <Fragment key={id}>
                    <MFlex direction="column" pt="2">
                      <MText mb="4" as="h5" fontSize="md" fontWeight="bold">
                        {name}{' '}
                        <MText as="span" fontWeight="normal">
                          ({PRODUCT_TYPE_DISPLAY_SHORT[productType]})
                        </MText>
                      </MText>

                      <MTable size="sm">
                        <MThead>
                          <MTr _hover={{ bg: 'transparent' }}>
                            <MTh px={2} py={1} w="4" />
                            <MTh px={2} py={1} w="8.5rem">
                              Pricing Model
                            </MTh>
                            <MTh px={2} py={1} w="8.25rem">
                              From
                            </MTh>
                            <MTh px={2} py={1} w="8.25rem">
                              Up to
                            </MTh>
                            <MTh px={2} py={1} w="8.5rem">
                              Amount
                            </MTh>
                            <MTh px={2} py={1} w="6rem">
                              Lock Price
                            </MTh>
                          </MTr>
                        </MThead>
                        <MTbody>
                          {prices.map(
                            (
                              { from, to, priceModel, amount, product },
                              productIndex: number,
                            ) => {
                              const formattedFrom = formatNumber(from, {
                                maximumFractionDigits: 0,
                              });

                              const formattedTo =
                                formatNumber(to, {
                                  maximumFractionDigits: 0,
                                }) || '∞';

                              return (
                                <MTr
                                  key={`${productIndex}-range-${id}`}
                                  _hover={{ bg: 'transparent' }}
                                >
                                  <MTd px={2} py={1} verticalAlign="top" w="4">
                                    <MFlex align="center" minH={8}>
                                      <MText color="tPurple.base">{`${
                                        productIndex + 1
                                      }.`}</MText>
                                    </MFlex>
                                  </MTd>
                                  <MTd px={2} py={1} verticalAlign="top">
                                    <MFlex align="center" minH="8">
                                      <MText>
                                        {
                                          OFFERINGS.PRICE_MODEL_OPTIONS[
                                            priceModel
                                          ]
                                        }
                                      </MText>
                                    </MFlex>
                                  </MTd>
                                  <MTd px={2} py={1} verticalAlign="top">
                                    <MFlex align="center" minH="8">
                                      <MText>{formattedFrom}</MText>
                                    </MFlex>
                                  </MTd>
                                  <MTd px={2} py={1} verticalAlign="top">
                                    <MFlex align="center" minH="8">
                                      <MText>{formattedTo}</MText>
                                    </MFlex>
                                  </MTd>
                                  <MTd px={2} py={1} verticalAlign="top">
                                    <MFlex align="center" minH="8">
                                      <MCurrency
                                        amount={
                                          priceModel === PriceModelEnum.CUSTOM
                                            ? productsObj.byId[product.id]
                                                ?.unitPrice
                                            : amount
                                        }
                                        options={{
                                          maximumFractionDigits: 'max',
                                          currency,
                                        }}
                                      />
                                    </MFlex>
                                  </MTd>
                                  <MTd px={2} py={1} textAlign="center">
                                    {priceModel !== PriceModelEnum.CUSTOM && (
                                      <MCheckbox
                                        isChecked={lockedState[id] ?? false}
                                        onChange={(e) =>
                                          handleToggleRow(id, e.target.checked)
                                        }
                                        aria-label={`Select ${name}`}
                                      />
                                    )}
                                  </MTd>
                                </MTr>
                              );
                            },
                          )}
                        </MTbody>
                      </MTable>
                    </MFlex>
                    {!isLastRow && <MDivider my="2" />}
                  </Fragment>
                );
              },
            )}
          </>
        )}
        <MFlex
          justifyContent="center"
          align="center"
          position="sticky"
          bottom="0"
          background="tWhite.base"
          padding="12px"
          borderTop="1px solid #e2e8f0"
          zIndex="10"
          mt="4"
        >
          <MButton onClick={handleCancel} variant="cancel" mr={2}>
            Cancel
          </MButton>
          <MButton
            variant="primary"
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={isSaving || !Object.values(lockedState).some(Boolean)}
          >
            Save
          </MButton>
        </MFlex>
      </Card.Body>
    </MCard>
  );
};
